import { take, put, all, call } from "redux-saga/effects";

import {
    SET_SESSION_DATA,
    CREATE_SESSION,
    LOAD_CONFIG,
    SET_IS_LOADING,
    START_IN_PRESET_SESSION,
} from "../../actions/sessionActions";
import {
    SessionInitData,
    SdsAccountInfo,
    BopDisabledFlagMode,
    BumpItem,
    StringMap,
    StringPair,
    ItemMenuPath,
} from "../../../api/generated/models";

import client from "../../../api/client/SdsWebApiClient";
import {
    getAppVersion,
    getFrontendSettings,
    loadAppCustomization,
    loadConfigurationFiles,
} from "../../../utilities/configUtils";
import {
    findStartInPreset,
    findFirstSetByModel,
    filterSetsRecursive,
    getConfigFile,
    getLanguage,
    getAccountId,
    parseSettingsFilter,
} from "../../../utilities/modelSelectorDataUtils";
import Constants from "../../../constants";
import { loadProductInfoSaga } from "./metadataSaga";
import { resolveShowProductInfo } from "./summaryDisplayFlags";
import { SummaryConfig } from "../../../models/summary/Config";
import TranslationMap from "../../../models/translationMap";
import { setFilterTypes } from "./filterTypeSaga";
export function* createSessionSaga(): any {
    while (true) {
        const action = yield take(CREATE_SESSION);

        const { initialConfig, configEncoding, surfaceProductArticleNo } = action;
        if (!initialConfig) {
            throw new Error("initialConfig is not set");
        }

        const clientSettingsPromise = client.getClientSettingsGet();
        const frontendSettingsPromise = getFrontendSettings();
        const appVersionPromise = getAppVersion();

        const searchParams = new URLSearchParams(window.location.search);
        const accountId = searchParams.get(Constants.accountUrlQueryParam);
        let market = searchParams.get(Constants.marketUrlQueryParam) || undefined;
        let language = searchParams.get(Constants.languageUrlQueryParam) || (yield client.getDefaultLanguageGet());
        language = language.toUpperCase();

        const sessionData: SessionInitData = yield client.createSessionPost(
            undefined,
            language,
            market,
            BopDisabledFlagMode.IgnoreDisabledFlag
        );

        let account: SdsAccountInfo | null = null;
        if (accountId) {
            account = yield client.setCurrentSdsAccountPost(sessionData.sessionId, accountId);
        }

        if (!account) {
            account = yield client.getCurrentSdsAccountGet(sessionData.sessionId);
        }

        const configsPromise = loadConfigurationFiles(language, account?.id);

        // Product info is loaded only here (with CREATE_SESSION) and preserved on RESET_FOR_MODEL_SELECTOR,
        // so LOAD_CONFIG never refetches it when reusing the same session.
        const [, [appConfig, summaryData], clientSettings, frontendSettings, appVersion]: [
            any,
            [Object, SummaryConfig],
            StringMap,
            any,
            string
        ] = yield all([
            loadProductInfoSaga(language, sessionData.localization, account?.id),
            configsPromise,
            clientSettingsPromise,
            frontendSettingsPromise,
            appVersionPromise,
        ]);

        // Convert StringMap (array of key/value pairs) to a simple object for easier lookups.
        const applicationSettings: Record<string, string> = {};
        clientSettings.dict.forEach((pair: StringPair) => {
            applicationSettings[pair.key] = pair.value;
        });

        // eslint-disable-next-line no-console
        console.log(`Configurator version: ${appVersion}`);

        let showPrice = applicationSettings["price.enable"] === "True";
        const hidePrice = searchParams.get("hide_price");
        if (
            (hidePrice && /true/i.test(hidePrice)) ||
            frontendSettings.hide_price === true ||
            /true/i.test(frontendSettings.hide_price)
        ) {
            showPrice = false;
        }

        // hide_product_info hides the product list AND the surface identifiers image on the summary
        // page (the single summary "hide" flag). Prices stay (governed by hide_price); action cards
        // are unaffected. The email PDF is unaffected — it still honours hide_price only.
        const showProductInfo = resolveShowProductInfo(searchParams, frontendSettings);

        let showFilters = frontendSettings.show_filters;
        const hideFilters = searchParams.get("show_filters");

        if (
            (hideFilters && /false/i.test(hideFilters)) ||
            frontendSettings.show_filters === false ||
            /false/i.test(frontendSettings.show_filters)
        ) {
            showFilters = false;
        } else if (showFilters === undefined && hideFilters && /true/i.test(hideFilters)) {
            showFilters = true;
        }

        let touchScreen = false;
        const isTouchScreen = searchParams.get("touch_screen");

        if (isTouchScreen && /true/i.test(isTouchScreen)) {
            touchScreen = true;
        }

        /* eslint-disable no-param-reassign */
        const guiTranslation = sessionData.bumps[0].items.reduce(
            (translations: { [key: string]: string }, item: BumpItem) => {
                if (item.shortText) translations[item.id] = item.shortText;
                return translations;
            },
            {}
        );

        const appCustomization = account ? yield loadAppCustomization(account) : null;
        if (showFilters) {
            yield setFilterTypes(frontendSettings, appCustomization);
        }

        yield put({
            type: SET_SESSION_DATA,
            sessionData,
            frontendSettings,
            appConfig,
            summaryData,
            applicationSettings,
            language,
            showPrice,
            showProductInfo,
            account,
            showFilters,
            guiTranslation: new TranslationMap(guiTranslation),
            appVersion,
            touchScreen,
            appCustomization,
        });

        yield put({
            type: LOAD_CONFIG,
            initialConfig,
            configEncoding,
            surfaceProductArticleNo,
        });
    }
}

export function* startInPresetSessionSaga(): any {
    while (true) {
        yield take(START_IN_PRESET_SESSION);
        yield put({ type: SET_IS_LOADING, isLoading: true });

        const url = new URL(window.location.href);
        const [language, accountId]: [string, string | null] = yield all([call(getLanguage), call(getAccountId)]);
        const configurationFiles = yield call([client, "getConfigurationFilesGet"], language, accountId ?? undefined);
        const introConfig = yield call(getConfigFile, configurationFiles.initFile);

        const setFilter = parseSettingsFilter(url.searchParams.get("settings"));
        const filteredConfig =
            setFilter && setFilter.length > 0
                ? { ...introConfig, sets: filterSetsRecursive(introConfig.sets, setFilter) }
                : introConfig;

        // Match ModelSelector precedence: surface_product (article-number auto-pick) wins
        // when both params are present. Mirror activateArticleNumber's swallow-and-fallthrough
        // semantics so a transient article-lookup failure still loads the default preset
        // instead of bouncing the default-preset entry to the selector.
        const autoPickProduct = url.searchParams.get("surface_product") || undefined;
        if (autoPickProduct) {
            try {
                const paths: ItemMenuPath[] = yield call(
                    [client, "findMenuPathsByArticleNoGet"],
                    autoPickProduct,
                    accountId ?? undefined,
                    true,
                    setFilter
                );
                if (paths.length > 0) {
                    const models = new Set(paths.map(p => p.model));
                    const set = findFirstSetByModel(filteredConfig.sets, models);
                    if (set) {
                        yield put({
                            type: CREATE_SESSION,
                            initialConfig: set.config,
                            surfaceProductArticleNo: autoPickProduct,
                        });
                        continue;
                    }
                }
            } catch (e) {
                console.error("[startInPresetSessionSaga] article lookup failed", e);
            }
            // No article match (or lookup failed) → fall through to default preset below.
        }

        const preset = findStartInPreset(filteredConfig);
        if (!preset) {
            // Empty intro.sets means the intro config is misconfigured — surface as a
            // fatal error rather than silently bouncing to the selector (which would
            // re-enter this saga via SessionInitFromUrl and loop).
            throw new Error("startInPresetSessionSaga: no preset found in intro configuration");
        }
        yield put({ type: CREATE_SESSION, initialConfig: preset.config });
    }
}
